import React, { createContext, useState, useCallback, useContext } from 'react';
import { Order, CartItem } from '../data/mockData';

interface OrdersContextType {
  orders: Order[];
  addOrder: (items: CartItem[], table?: string) => void;
  updateOrderStatus: (orderId: string, status: Order['status']) => void;
  getNextOrderNumber: () => number;
}

const OrdersContext = createContext<OrdersContextType | undefined>(undefined);

export function OrdersProvider({ children }: { children: React.ReactNode }) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [orderCounter, setOrderCounter] = useState(1);

  const getNextOrderNumber = useCallback(() => {
    return orderCounter;
  }, [orderCounter]);

  const addOrder = useCallback((items: CartItem[], table?: string) => {
    const total = items.reduce((acc, item) => {
      const itemTotal = item.product.price * item.quantity;
      const modifiersTotal = item.modifiers.reduce((m, mod) => m + mod.price, 0) * item.quantity;
      return acc + itemTotal + modifiersTotal;
    }, 0);

    const newOrder: Order = {
      id: `ord-${Date.now()}`,
      orderNumber: orderCounter,
      table,
      waiter: 'Cajero 1',
      items,
      status: 'pending',
      createdAt: new Date(),
      total: total * 1.18,
    };

    setOrders((prev) => [newOrder, ...prev]);
    setOrderCounter((prev) => prev + 1);
  }, [orderCounter]);

  const updateOrderStatus = useCallback((orderId: string, status: Order['status']) => {
    setOrders((prev) =>
      prev.map((order) =>
        order.id === orderId ? { ...order, status } : order
      )
    );
  }, []);

  const value: OrdersContextType = {
    orders,
    addOrder,
    updateOrderStatus,
    getNextOrderNumber,
  };

  return (
    <OrdersContext.Provider value={value}>
      {children}
    </OrdersContext.Provider>
  );
}

export function useOrders(): OrdersContextType {
  const context = useContext(OrdersContext);
  if (context === undefined) {
    throw new Error('useOrders must be used within OrdersProvider');
  }
  return context;
}
